import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { api } from "@/lib/api";

/**
 * Identidad del ambiente (desarrollo, QA o producción) desde el punto de
 * vista de la interfaz.
 *
 * Personal de NPMX entró a QA creyendo que era producción y reportó una
 * incidencia sobre datos de prueba. Desde entonces todo lo que no sea
 * producción se rotula de forma inconfundible: banda permanente, insignia en
 * el menú, prefijo en el título de la pestaña y favicon distinto.
 *
 * El nombre viene horneado en el build (`VITE_SICEN_AMBIENTE`), como el resto
 * de la configuración del frontend. Si el build no lo declara, se infiere de
 * los hosts que se saben no productivos, para que un QA construido sin la
 * variable no pase por producción. Cualquier otro host sin declaración se
 * considera producción: el error de rotular producción como pruebas sería
 * peor que el contrario.
 */

/** Hosts que se saben no productivos aunque el build no lo declare. */
const HOSTS_CONOCIDOS: Record<string, string> = {
  "dev.sicen.abc-ti.com": "qa",
  localhost: "dev",
  "127.0.0.1": "dev",
};

export function resolverAmbiente(valorBuild: string | undefined, hostname: string): string {
  const declarado = (valorBuild ?? "").trim().toLowerCase();
  if (declarado) return declarado;
  return HOSTS_CONOCIDOS[(hostname ?? "").trim().toLowerCase()] ?? "prod";
}

export function esProductivo(nombre: string): boolean {
  const n = (nombre ?? "").trim().toLowerCase();
  return n === "" || n === "prod" || n === "produccion" || n === "producción" || n === "production";
}

/** Rótulo largo para bandas y pantallas; null en producción. */
export function etiquetaDe(nombre: string): string | null {
  if (esProductivo(nombre)) return null;
  switch (nombre.trim().toLowerCase()) {
    case "qa":
      return "Ambiente de pruebas (QA)";
    case "dev":
      return "Ambiente de desarrollo";
    default:
      return `Ambiente no productivo (${nombre})`;
  }
}

/** Rótulo corto para insignias y el título de la pestaña; null en producción. */
export function siglaDe(nombre: string): string | null {
  if (esProductivo(nombre)) return null;
  return nombre.trim().toUpperCase();
}

export const AMBIENTE = resolverAmbiente(
  import.meta.env.VITE_SICEN_AMBIENTE,
  typeof window !== "undefined" ? window.location.hostname : "",
);
export const AMBIENTE_PRODUCTIVO = esProductivo(AMBIENTE);
export const ETIQUETA_AMBIENTE = etiquetaDe(AMBIENTE);
export const SIGLA_AMBIENTE = siglaDe(AMBIENTE);
export const URL_PRODUCCION: string =
  import.meta.env.VITE_SICEN_URL_PRODUCCION || "https://sicen.npmx.org";

/**
 * Marca el documento con el ambiente: prefijo en el título de la pestaña y
 * favicon propio, para que el ambiente se distinga incluso con la pestaña en
 * segundo plano o entre muchas abiertas. En producción no toca nada.
 */
export function marcarDocumentoConAmbiente(doc: Document = document): void {
  const sigla = SIGLA_AMBIENTE;
  if (!sigla) return;
  const prefijo = `[${sigla}] `;
  if (!doc.title.startsWith(prefijo)) {
    doc.title = prefijo + doc.title;
  }
  const icono = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icono) {
    icono.href = "/sicen-mark-qa.svg";
  }
}

export interface AmbienteInfo {
  nombre: string;
  etiqueta: string | null;
  productivo: boolean;
  restringeInternos: boolean;
  urlProduccion: string;
}

export interface AccesoAmbiente {
  permitido: boolean;
  motivo: string | null;
  correo: string | null;
  ambiente: AmbienteInfo;
}

export const ACCESO_AMBIENTE_QUERY_KEY = ["ambiente", "acceso"] as const;

/** Código con el que el backend marca el 403 por ambiente restringido. */
export const CODIGO_AMBIENTE_RESTRINGIDO = "AMBIENTE_RESTRINGIDO";
/** Evento que el cliente HTTP emite al recibir ese 403, para que la pantalla de bloqueo aparezca. */
export const EVENTO_AMBIENTE_RESTRINGIDO = "sicen:ambiente-restringido";

/**
 * ¿Puede la persona que inició sesión operar en este ambiente?
 *
 * Lo decide el backend sobre la identidad real (antes de cualquier
 * suplantación). Si la consulta falla por red o por caída del backend se deja
 * pasar: la restricción la aplica el servidor en cada petición, aquí solo se
 * explica con una pantalla clara en lugar de una lluvia de 403.
 */
export function useAccesoAmbiente() {
  const auth = useAuth();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ACCESO_AMBIENTE_QUERY_KEY,
    queryFn: async () => (await api.get<AccesoAmbiente>("/api/ambiente/acceso")).data,
    enabled: !!auth.user?.access_token,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    const alRestringir = () => {
      void qc.invalidateQueries({ queryKey: ACCESO_AMBIENTE_QUERY_KEY });
    };
    window.addEventListener(EVENTO_AMBIENTE_RESTRINGIDO, alRestringir);
    return () => window.removeEventListener(EVENTO_AMBIENTE_RESTRINGIDO, alRestringir);
  }, [qc]);

  return {
    cargando: query.isLoading,
    bloqueado: query.data?.permitido === false,
    acceso: query.data ?? null,
  };
}
